import { useState } from 'react';
import { formatRelativeTime } from "../utils/dateUtils";
import Pagination from '../components/Pagination';


function JobRow({ job, isOpen, onToggle }) {
    const statusColor =
        job.status === "COMPLETED"
            ? "has-background-success"
            : job.status === "QUEUED"
                ? "has-background-warning"
                : job.status === "RUNNING"
                    ? "has-background-info"
                    : "has-background-danger";

    const progress =
        job.total_tasks && job.completedRuns
            ? (job.completedRuns / job.total_tasks) * 100
            : 0;

    return (
        <div key={job.id} className="box mb-2">
            <div className="columns is-vcentered is-multiline">
                {/* Status Rail */}
                <div className="column is-narrow">
                    <div
                        className={statusColor}
                        style={{
                            width: "6px",
                            height: "100%",
                            borderRadius: "3px",
                        }}
                    />
                </div>

                {/* Identity / Circuit */}
                <div className="column is-3">
                    <strong>{job.snapshots.circuits.name}</strong>{" "}
                    <span className="has-text-grey">
                        {job.snapshots.circuits.frameworks.name}{" "}
                        {job.snapshots.circuits.frameworks.version}
                    </span>
                </div>

                {/* Primary Params */}
                <div className="column is-3 has-text-grey">
                    <code>{job.configuration.machine}</code> · reps{" "}
                    {job.configuration.reps} · tasks {job.total_tasks || "—"}
                </div>

                {/* Meta / Timestamp */}
                <div className="column is-2 has-text-grey is-size-7">
                    snapshot <code>{job.snapshots.hash.slice(0, 8)}</code> ·{" "}
                    {new Date(job.created_at).toLocaleDateString()}
                </div>

                {/* Dynamic Status / Progress */}
                <div className="column is-2">
                    {job.status === "RUNNING" && (
                        <div className="is-flex is-align-items-center">
                            <i className="fas fa-spinner fa-spin mr-2 has-text-info" />
                            <span className="is-size-7">
                                {job.completedRuns}/{job.total_tasks || "?"} runs
                            </span>
                            {job.total_tasks && (
                                <progress
                                    className="progress is-small is-info ml-2"
                                    value={job.completedRuns}
                                    max={job.total_tasks}
                                />
                            )}
                        </div>
                    )}
                    {job.status === "QUEUED" && (
                        <span className="is-size-7 has-text-warning">Queued...</span>
                    )}
                    {job.status === "COMPLETED" && (
                        <span className="is-size-7 has-text-success">Completed</span>
                    )}
                    {job.status === "FAILED" && (
                        <span className="is-size-7 has-text-danger">Failed</span>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="column is-2 buttons are-small">
                    {job.status === "RUNNING" && (
                        <button className="button is-small is-danger">
                            <i className="fas fa-stop mr-1" /> Cancel
                        </button>
                    )}
                    {(job.status === "COMPLETED" || job.status === "FAILED") && (
                        <button className="button is-small is-primary">
                            <i className="fas fa-redo mr-1" /> Re-run
                        </button>
                    )}
                    {job.status === "COMPLETED" && (
                        <button className="button is-small is-link">
                            <i className="fas fa-file-alt mr-1" /> View Report
                        </button>
                    )}
                </div>

                {/* Shelf Contents: Input files, Curves, Backends */}
                <div className="column is-full mt-2">
                    <div className="columns is-multiline">
                        {/* Input Files */}
                        <div className="column is-4">
                            <strong>Input files</strong>
                            <div className="tags mt-1">
                                {job.configuration.inputFiles.map((f) => (
                                    <span key={f} className="tag is-light">
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Curves */}
                        <div className="column is-4">
                            <strong>Curves</strong>
                            <div className="tags mt-1">
                                {job.configuration.curves.map((c) => (
                                    <span key={c} className="tag is-info is-light">
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Backends */}
                        <div className="column is-4">
                            <strong>Backends</strong>
                            <div className="tags mt-1">
                                {job.configuration.backends.map((b) => (
                                    <span key={b} className="tag is-dark is-light">
                                        {b}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


const JobsList = ({ jobs, onCancelJob }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedJob, setSelectedJob] = useState(null);
    const [openJobId, setOpenJobId] = useState(null);
    const [viewMode, setViewMode] = useState('table');

    const itemsPerPage = 8;

    const totalPages = Math.ceil(jobs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentJobs = jobs.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const toggleViewMode = () => {
        setViewMode(viewMode === 'list' ? 'table' : 'list');
    };

    // ---------------------------------------------
    // Render Status Tags
    // ---------------------------------------------
    const renderStatus = (status) => {
        switch (status) {
            case "SUBMITTED":
                return <span className="tag">Submitted</span>;
            case "QUEUED":
                return <span className="tag is-warning">Queued</span>;
            case "RUNNING":
                return (
                    <span className="tag has-background-info-light">
                        <span className="mr-2 pulse-ring has-background-primary" style={{ width: "0.5rem", height: "0.5rem", borderRadius: "50%" }} />
                        Running
                    </span>
                );
            case "COMPLETED":
                return <span className="tag is-success">Completed</span>;
            case "CANCELLED":
                return <span className="tag is-light">Cancelled</span>;
            case "FAILED":
                return <span className="tag is-danger">Failed</span>;
            default:
                return <span className="tag is-dark">Unknown</span>;
        }
    };


    const renderTableView = () => {
        return (
            <div className="box">
                <table className="table is-fullwidth is-hoverable circuits-table">
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th>Circuit</th>
                            <th>Framework</th>
                            <th>Machine</th>
                            <th>Backend</th>
                            <th>Curve</th>
                            <th>Input</th>
                            <th>Reps.</th>
                            <th>Tasks</th>
                            <th>Submitted At</th>
                        </tr>
                    </thead>

                    <tbody>
                        {currentJobs.map((job, index) => (
                            <tr key={job.id}>
                                <td>{renderStatus(job.status)}</td>
                                <td><a className='has-text-black is-underlined' href={`/circuits/${job.snapshots?.circuits?.id}`}>{job.snapshots?.circuits.name}</a></td>
                                <td>{job.snapshots?.circuits.frameworks.name + ' ' + job.snapshots?.circuits?.frameworks?.version}</td>
                                <td>
                                    <code>{job.configuration.machine}</code>
                                </td>
                                <td>
                                    {job.configuration.backends.map((backend, i) => (
                                        <span key={backend + '-' + i} className="tag is-danger is-primary is-radiusless mr-1">
                                            {backend}
                                        </span>
                                    ))}
                                </td>
                                <td>
                                    {job.configuration.curves.map((c, i) => (
                                        <span key={i} className="tag is-info is-radiusless mr-1">
                                            {c}
                                        </span>
                                    ))}
                                </td>
                                <td>
                                    {job.configuration.inputFiles.map((c, i) => (
                                        <span key={i} className="tag is-radiusless mr-1">
                                            {c}
                                        </span>
                                    ))}
                                </td>
                                <td>{job.configuration.reps}</td>
                                <td>{job.total_tasks > 0 ? job.total_tasks : '—'}</td>
                                <td>{formatRelativeTime(job.created_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderCardView = () => {

        return (
            <>
                {currentJobs.map((job) => (
                    <JobRow
                        key={job.id}
                        job={job}
                        isOpen={openJobId === job.id}
                        onToggle={() =>
                            setOpenJobId(openJobId === job.id ? null : job.id)
                        }
                    />
                ))}
            </>
        );
    };


    return (
        <>
            {
                jobs.length === 0 ? (
                    <div className="columns is-vcentered" style={{ height: "100%", width: "100%", textAlign: "center" }}>
                        <div className="column">
                            <p className="is-size-2 has-text-weight-light is-unselectable">You have no submitted jobs</p>
                        </div>
                    </div>
                ) :
                    (<>
                        <div className="level">
                            <div className="level-left"></div>
                            <div className="level-right">
                                <button
                                    className="button is-outlined is-dark"
                                    onClick={toggleViewMode}
                                >
                                    <span className="icon">
                                        {viewMode === 'list' ? (
                                            <i className="fas fa-th-large"></i>
                                        ) : (
                                            <i className="fas fa-list"></i>
                                        )}
                                    </span>
                                    <span>{viewMode === 'list' ? 'Table View' : 'List View'}</span>
                                </button>
                            </div>
                        </div>
                        <div className="content" style={{ height: "600px" }}>
                            {viewMode === 'table' ? renderTableView() : renderCardView()}
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </>)
            }
        </>
    );
};

export default JobsList;
